import React from "react";
import PropTypes from "prop-types";
import {
  formatTimelineDate,
  formatTimelineTime,
} from "../../../util/timelineHelper";
import StatusIcon from "./StatusIcon";
import HopDropEntry from "./HopDropEntry";
import IngredientEntry from "./IngredientEntry";

export const renderEntryTime = (entry) => {
  if (
    !entry.startTime ||
    !(entry.startTime instanceof Date) ||
    isNaN(entry.startTime.getTime())
  ) {
    return null;
  }

  const hasEndTime =
    entry.endTime &&
    entry.endTime instanceof Date &&
    !isNaN(entry.endTime.getTime());

  const showDate = (d1, d2) => {
    if (!d2) {
      return d1.toDateString() !== new Date().toDateString();
    }
    return d1.toDateString() !== d2.toDateString();
  };

  const format = (d, includeDate) => {
    if (includeDate) {
      return formatTimelineDate(d);
    }
    return formatTimelineTime(d);
  };

  const sessionStartDay = showDate(entry.startTime);

  if (entry.status === "completed") {
    if (hasEndTime && entry.startTime.getTime() !== entry.endTime.getTime()) {
      const incDate =
        showDate(entry.startTime, entry.endTime) || sessionStartDay;
      const startStr = format(entry.startTime, incDate);
      const endStr = format(entry.endTime, incDate);
      if (startStr !== endStr) {
        return `${startStr} - ${endStr}`;
      }
    }

    return format(entry.startTime, sessionStartDay);
  }

  if (entry.status === "active") {
    if (hasEndTime && entry.startTime.getTime() !== entry.endTime.getTime()) {
      const incDate =
        showDate(entry.startTime, entry.endTime) || sessionStartDay;
      const startStr = format(entry.startTime, incDate);
      const endStr = format(entry.endTime, incDate);
      if (startStr !== endStr) {
        return `${startStr} - Est ${endStr}`;
      }
    }

    return format(entry.startTime, sessionStartDay);
  }

  // upcoming
  return `Est ${format(entry.startTime, sessionStartDay)}`;
};

/**
 * A single timeline entry (mash step, boil, fermentation step, etc.)
 */
const TimelineEntry = ({ entry }) => {
  const statusClass =
    entry.status === "active"
      ? "brew-timeline__entry--active"
      : entry.status === "completed"
        ? "brew-timeline__entry--completed"
        : entry.status === "warning"
          ? "brew-timeline__entry--warning"
          : entry.status === "failed"
            ? "brew-timeline__entry--failed"
            : "";

  return (
    <div className={`brew-timeline__entry ${statusClass}`}>
      <div className="brew-timeline__entry-header">
        <span className="brew-timeline__entry-label">{entry.label}</span>
        <StatusIcon status={entry.status} />
      </div>
      <div className="brew-timeline__entry-meta">
        {entry.temperature != null && (
          <span className="brew-timeline__entry-temp">
            {entry.temperature}°C
          </span>
        )}
        <span>{entry.durationLabel}</span>
        {entry.startTime && (
          <span className="brew-timeline__entry-time">
            {renderEntryTime(entry)}
          </span>
        )}
      </div>

      {/* Mash ingredients */}
      {entry.ingredients && entry.ingredients.length > 0 && (
        <div className="brew-timeline__children">
          {entry.ingredients.map((ing, idx) => (
            <IngredientEntry key={idx} ingredient={ing} />
          ))}
        </div>
      )}

      {/* Hop drops / fermentation additions */}
      {entry.children && entry.children.length > 0 && (
        <div className="brew-timeline__children">
          {entry.children.map((child) => (
            <HopDropEntry key={child.id} child={child} />
          ))}
        </div>
      )}
    </div>
  );
};

TimelineEntry.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    label: PropTypes.string,
    temperature: PropTypes.number,
    durationLabel: PropTypes.string,
    startTime: PropTypes.instanceOf(Date),
    endTime: PropTypes.instanceOf(Date),
    ingredients: PropTypes.array,
    children: PropTypes.array,
  }).isRequired,
};

export default TimelineEntry;
